import React from 'react';
import { connect, Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { Route } from 'react-router-dom';
import { Redirect, RouteComponentProps, Switch } from 'react-router';
import classNames from 'classnames';

import { CatalogueTemplate } from '../../shop/catalogue/CatalogueTemplate';
import ProductDetailRoute from '../product/detail/ProductDetailRoute';
import { CheckoutThanks } from '../../shop/checkout/CheckoutThanks';
import { OrdersView } from '../../shop/orders/OrdersView';
import OrdersDetailsView from '../../shop/orders/OrdersDetailsView';
import { BundleDetailPage } from '../bundles/BundleDetailPage';
import { PageNotFound } from '../../shop/PageNotFound';
import { AssistantPage } from '../../shop/assistant/AssistantPage';
import { history } from '../../configureStore';
import NotificationContainer from '../notification/NotificationContainer';
import { HeaderWrapper } from '../header/HeaderWrapper';
import { HeaderContainer } from '../header/HeaderContainer';
import LibxRouteSwitch from '../../yourborrowbox/libx/LibxRouteSwitch';
import withErrorLog from '../../shared/utils/withErrorLog';
import TermsAndConditions from '../../shop/termsandconditions/TermsAndConditions';
import RouteSwitch from '../../yourborrowbox/collection/RouteSwitch';
import LocalContentRouteSwitch from '../../yourborrowbox/localcontent/LocalContentRouteSwitch';
import LanguageRouteSwitch from '../../shop/language/RouteSwitch';
import CampaignRouteSwitch from '../../shop/unlimited/RouteSwitch';
import MarketingRouteSwitch from '../../yourborrowbox/marketing/MarketingRouteSwitch';
import Footer from './Footer';
import DocumentTitle from './DocumentTitle';
import { MODAL_ROOT_CLASS } from './modal/ModalLayout';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CheckRequiredSpoc, SpocRequiredFor } from '../common/CheckRequiredSpoc/CheckRequiredSpoc';
import { EnvironmentProvider } from './EnvironmentContext';
import { MaintenanceWrapper } from '../../shop/MaintenanceWrapper';
import { QueryClientProvider } from '@tanstack/react-query';
import { browserClient } from '../../react-query';
import { CartCheckout } from '../../shop/cart/checkout/CartCheckout';
import { ScrollRestoration } from '../utils/scroll/ScrollRestoration';
import { SelectionListPage } from '../../shop/selectionlists/SelectionListPage';
import { SearchResultPage } from '../search/SearchResultPage';
import { CartPage } from '../../shop/cart/CartPage';
import { ReportingPage } from '../../yourborrowbox/reporting/ReportingPage';
import { DropdownProvider } from '../../hooks/dropdownContext';
import { LOCALISATION, useFeature } from '../utils/togglz';
import { useTranslation } from 'react-i18next';

//TODO: move to core
const App = props => {
    const loadingClasses = classNames({ sticky: false });
    const isLocalisationEnabled = useFeature(LOCALISATION);
    const { i18n, t } = useTranslation();

    if (!isLocalisationEnabled && i18n.language !== 'en-AU') {
        i18n.changeLanguage('en-AU').then();
    }

    return (
        <QueryClientProvider client={browserClient}>
            <Provider store={props.store}>
                <ConnectedRouter history={history}>
                    <MaintenanceWrapper active={props.maintenance}>
                        <EnvironmentProvider>
                            <DropdownProvider>
                                <ScrollRestoration>
                                    <DocumentTitle title={t('components.documentTitle.shop')} />
                                    <div id={MODAL_ROOT_CLASS} />
                                    <div className={loadingClasses} id="loading-root" />
                                    <NotificationContainer />
                                    <HeaderWrapper {...props} reload={props.reload}>
                                        <HeaderContainer />
                                    </HeaderWrapper>
                                    <div className="content-area">
                                        <Switch>
                                            <Redirect exact from="/" to="/catalogue" />
                                            <Redirect exact from="/index.html" to="/catalogue" />
                                            <Route path="/catalogue/:loanFormat?" component={CatalogueTemplate} />
                                            <Route exact path="/products/:productId" component={ProductDetailRoute} />
                                            <Route
                                                path="/orderlists/:id"
                                                render={(props: RouteComponentProps<{ readonly id: string }>) => (
                                                    <CheckRequiredSpoc
                                                        type={SpocRequiredFor.ORDERLIST}
                                                        id={props.match.params.id}
                                                    >
                                                        <SelectionListPage />
                                                    </CheckRequiredSpoc>
                                                )}
                                            />

                                            <Route exact path="/bundles/:bundleId" component={BundleDetailPage} />
                                            <Route exact path="/cart" component={CartPage} />
                                            <Route exact path="/checkout" component={CartCheckout} />
                                            <Route exact path="/checkout/thanks" component={CheckoutThanks} />
                                            <Route exact path="/orders" component={OrdersView} />
                                            <Route
                                                exact
                                                path="/orders/:orderId"
                                                render={(props: RouteComponentProps<{ readonly orderId: string }>) => (
                                                    <CheckRequiredSpoc
                                                        type={SpocRequiredFor.ORDER}
                                                        id={props.match.params.orderId}
                                                    >
                                                        <OrdersDetailsView {...props} />
                                                    </CheckRequiredSpoc>
                                                )}
                                            />
                                            <Route
                                                exact
                                                path="/search/:pathParam1?/:pathParam2?"
                                                component={SearchResultPage}
                                            />
                                            <Route path="/assistant" component={AssistantPage} />
                                            <Route path="/reporting*" component={ReportingPage} />
                                            <Route path="/collection" component={RouteSwitch} />
                                            <Route path="/localcontent" component={LocalContentRouteSwitch} />
                                            <Route exact path="/terms-conditions" component={TermsAndConditions} />
                                            <Route path="/libxchange" component={LibxRouteSwitch} />
                                            <Route path="/language" component={LanguageRouteSwitch} />
                                            <Route path="/unlimited" component={CampaignRouteSwitch} />
                                            <Route path="/marketing" component={MarketingRouteSwitch} />
                                            <Route component={PageNotFound} />
                                        </Switch>
                                    </div>
                                    <div style={{ clear: 'both', display: 'block' }} />
                                    <Footer />
                                </ScrollRestoration>
                            </DropdownProvider>
                        </EnvironmentProvider>
                    </MaintenanceWrapper>
                </ConnectedRouter>
                {process.env.REACT_APP_DISABLE_QUERY_DEVTOOLS ? null : <ReactQueryDevtools initialIsOpen={false} />}
            </Provider>
        </QueryClientProvider>
    );
};

const mapStateToProps = state => ({
    reload: state.header.reload,
    authentication: state.authentication
});

export default connect(mapStateToProps)(withErrorLog(App));
